/**
 * Creates a concurrency-limited executor.
 * At most `limit` async tasks run simultaneously; the rest queue up.
 *
 * Usage:
 *   const limit = createConcurrencyLimiter(8);
 *   const results = await Promise.allSettled(items.map(item => limit(() => fetchItem(item))));
 */
export function createConcurrencyLimiter(limit: number) {
  let active = 0;
  const queue: (() => void)[] = [];

  return function <T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        active++;
        fn()
          .then(resolve, reject)
          .finally(() => {
            active--;
            if (queue.length > 0) {
              queue.shift()!();
            }
          });
      };

      if (active < limit) {
        run();
      } else {
        queue.push(run);
      }
    });
  };
}
